import styled from '@emotion/styled';
import { Field, Form, Formik, FormikErrors, FormikTouched } from 'formik';
import React, { Suspense, useEffect } from 'react';
import * as Yup from 'yup';
import { colors } from '../../components/baseStyle/Variables';
import Button from '../../components/button/Button';
import RevalidateOnLanguageChange from '../../components/formElements/RevalidateOnLanguageChange';
import FormWrapper from '../../components/formWrapper/FormWrapper';
import TextField from '../../components/inputs/TextField';
import LeaseTermsTooltip from '../../components/lease-terms-tooltip/lease-terms-tooltip';
import PageLoader from '../../components/pageLoader/PageLoader';
import { Application, ChannelTypes, Dealer, Details, FormTypes, SendVerificationTokenError } from '../../gql/graphql';
import { getDealerFriendlyName } from '../../helpers/converters/getDealerFriendlyName';
import { getDisclosureUrl, getQuillDisclosureUrl, DisclosureLink, QuillDisclosureLink } from '../../constants/disclosure-links';
import { identify, SegmentProps } from '../../state-machine/actions/segment';
import { CustomerLookupEvents } from '../../state-machine/types/customerLookupMachine.types';
import { isEnumValue, parseEnumValue } from '../../helpers/enum/enum.helpers';
import { Role } from '../../types/role';
import { isLongForm as isDealerLongForm } from '../../helpers/converters/isLongForm';
import { Trans, useTranslation } from 'react-i18next';
import { getApplicationFinancingType } from '../../helpers/converters/getApplicationFinancingType';
import { ApplicationFinancingType } from '../../constants/application-financing-type';

const SkipOtpLinkButton = styled.button`
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: ${colors.koalafiBlue};
  text-decoration: underline;
  cursor: pointer;
`;

export enum AppFlowType {
  AvailableApproval = 'AvailableApproval', // Application with available approval
}

interface OTPFormModel {
  cellPhone: string;
}

export type OTPRequestProps = SegmentProps & {
  onNext: any;
  onSkip?: any;
  isLoading: boolean;
  dealer: Dealer;
  application: Application | undefined;
  orderDetails: Details;
  cellPhone: string;
  codeErrors?: SendVerificationTokenError[];
  role?: Role;
  otpBypassWhitelisted?: boolean;
};

const isVirtualCardAppFinished = (application?: Application) => {
  switch (application?.lease?.status) {
    case 'readyForFunding':
    case 'approvedForFunding':
    case 'funded':
    case 'docsSigned':
      return true;
    default:
      return false;
  }
};

const OTPRequest: React.FC<OTPRequestProps> = ({
  onNext,
  isLoading,
  application,
  dealer,
  orderDetails,
  codeErrors,
  cellPhone,
  role,
  otpBypassWhitelisted,
  track,
  page,
}) => {
  const isVirtualCard = dealer?.isVirtualCard;
  const isBrickAndMortar = dealer?.channelType === ChannelTypes.BrickAndMortar;
  const dealerFinancingType = dealer?.financingType;
  const orderCellPhone = orderDetails?.customer?.cellPhone;
  const isShortForm = dealer?.formType === FormTypes.Short;
  const isLongForm = isDealerLongForm(dealer);
  const friendlyName = getDealerFriendlyName(dealer);
  const { t } = useTranslation();

  const phoneFromParams = new URLSearchParams(document.location.search.substring(1)).get('phone');
  const appFlowTypeFromParams = new URLSearchParams(document.location.search.substring(1)).get('appFlowType');

  // used to track if customer search was called (i.e. new skip OTP flow followed) until XState refactor

  const cellPhoneErrorMsg = t(($) => $.error.enterValidNumber);
  const validationSchema = () => {
    return Yup.object().shape({
      cellPhone: Yup.string()
        .required(cellPhoneErrorMsg)
        .matches(/^\d{10}$/, cellPhoneErrorMsg), // only digits, exactly 10 characters
    });
  };

  //used to determine error message to display to user
  const typeNameErrorMapper = () => {
    let errMessage: string | undefined;
    if (codeErrors) {
      // if user navigates back screens and come back, we don't want error to display.
      if (codeErrors.some((error) => error.__typename === 'SMSNotSent')) {
        errMessage = t(($) => $.error.smsNotSent);
      } else if (codeErrors.some((error) => error.__typename === 'MaxAttempts')) {
        errMessage = t(($) => $.error.tooManyIncorrectAttemptsRetry);
      }
    }
    return errMessage;
  };

  const handleSubmit = async (e: OTPFormModel) => {
    identify(undefined, { cellPhone: e.cellPhone });
    track('Mobile Number Entered', { cellPhone: e.cellPhone });
    onNext({ type: CustomerLookupEvents.SEND_TOKEN, data: e });
  };

  useEffect(() => {
    page('OTP Request');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  let isAppFlowTypeAvailableApproval = false;
  if (!!appFlowTypeFromParams && isEnumValue(AppFlowType, appFlowTypeFromParams)) {
    const origin = parseEnumValue(AppFlowType, appFlowTypeFromParams);
    if (!!origin) {
      isAppFlowTypeAvailableApproval = origin === AppFlowType.AvailableApproval;
    }
  }

  const financingType = getApplicationFinancingType(application, dealer);
  const isInstallmentLoan = financingType === ApplicationFinancingType.installmentLoan;

  const Title = () => {
    if (isVirtualCard && isVirtualCardAppFinished(application)) {
      return <h1>{t(($) => $.common.welcomeBack)}</h1>;
    } else if (isAppFlowTypeAvailableApproval) {
      return (
        <h1>
          {t(($) => $.pageOtpRequest.useAvailableApproval)} {friendlyName}
        </h1>
      );
    } else {
      return (
        <h1>
          {t(($) => $.pageOtpRequest.payOverTimeAt)} {friendlyName}
        </h1>
      );
    }
  };

  const Subtitle = () => {
    if (isVirtualCard && isVirtualCardAppFinished(application)) {
      return <h6>{t(($) => $.pageOtpRequest.enterMobileNumberVirtualCard)}</h6>;
    }

    if (isAppFlowTypeAvailableApproval) {
      return <h6>{t(($) => $.pageOtpRequest.enterMobileNumberAtStore)}</h6>;
    }

    if (isInstallmentLoan) {
      return <h6>{t(($) => $.pageOtpRequest.financingOptionsInstallment)}</h6>;
    }

    return (
      <h6>
        {isShortForm && (
          <Trans
            i18nKey={($) => $.pageOtpRequest.financingOptionsShortForm}
            components={{
              1: <LeaseTermsTooltip styling={{ fontSize: '1rem' }} shortened={false} />,
              2: <strong />,
            }}
          />
        )}
        {isLongForm && (
          <Trans
            i18nKey={($) => $.pageOtpRequest.financingOptionsLongForm}
            components={{
              1: <LeaseTermsTooltip styling={{ fontSize: '1rem' }} shortened={false} />,
            }}
          />
        )}
      </h6>
    );
  };

  const shouldShowSkipOtpLink = () => {
    return otpBypassWhitelisted && isBrickAndMortar && dealerFinancingType === 'lto_only' && role !== 'Customer';
  };

  // If the user clicks to skip OTP, we still want to validate that they input a phone number
  const validateFormBeforeContinuing = (
    setTouched: (
      touched: FormikTouched<{ cellPhone: string | undefined }>,
      shouldValidate?: boolean,
    ) => Promise<void | FormikErrors<{ cellPhone: string | undefined }>>,
    validateForm: (values?: any) => Promise<FormikErrors<{ cellPhone: string | undefined }>>,
    values: { cellPhone: string | undefined },
  ) => {
    setTouched({ cellPhone: true }, true);
    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0 && values.cellPhone) {
        track('Clicked link to apply without phone');
        identify(undefined, { cellPhone: values.cellPhone });
        onNext({
          type: CustomerLookupEvents.SKIP_OTP,
          data: {
            cellPhone: values.cellPhone,
          },
        });
      }
    });
  };

  return (
    <Suspense fallback={<PageLoader />}>
      <FormWrapper>
        <Title />
        <Subtitle />

        <Formik
          onSubmit={handleSubmit}
          initialValues={{ cellPhone: cellPhone || phoneFromParams || orderCellPhone || '' }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ setTouched, validateForm, values }) => (
            <Form>
              <RevalidateOnLanguageChange />
              <Field
                name="cellPhone"
                id="cellPhone"
                generalErrorMessage={typeNameErrorMapper}
                type="tel"
                inputMode="numeric"
                label={t(($) => $.pageOtpRequest.enterApplicantMobileNumber)}
                component={TextField}
                useRawValue={true}
                autoFocus
                cleaveOptions={{
                  blocks: [3, 3, 4],
                  delimiters: ['-'],
                }}
                data-testid="otp-phone"
              />
              <p style={{ fontSize: '0.725rem', color: colors.darkMidGray }}>{t(($) => $.pageOtpRequest.verificationCodeInstructions)}</p>
              <Button type="submit" data-testid="nextButton" dataLoading={isLoading}>
                {t(($) => $.buttonLabels.continue)}
              </Button>
              <p>
                {isInstallmentLoan ? (
                  <Trans
                    i18nKey={($) => $.pageOtpRequest.byContinuingAgreementInstallment}
                    components={{
                      esignLink: (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={getDisclosureUrl(DisclosureLink.ElectronicCommunicationsInstallment)}
                          onClick={() => track('E-Sign Agreement Viewed')}
                        >
                          Communications Agreement
                        </a>
                      ),
                      privacyLink: (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={getDisclosureUrl(DisclosureLink.PrivacyPolicy)}
                          onClick={() => track('Privacy Policy Viewed')}
                        >
                          Privacy Policy
                        </a>
                      ),
                      quillLink: (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={getQuillDisclosureUrl(QuillDisclosureLink.PrivacyPolicy)}
                          onClick={() => track('Quill Privacy Policy Viewed')}
                        >
                          Quill Bank's Privacy Policy
                        </a>
                      ),
                    }}
                  />
                ) : (
                  <Trans
                    i18nKey={($) => $.pageOtpRequest.byContinuingAgreementLease}
                    components={{
                      esignLink: (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={getDisclosureUrl(DisclosureLink.ElectronicCommunications)}
                          onClick={() => track('E-Sign Agreement Viewed')}
                        >
                          Koalafi's E-Sign Agreement
                        </a>
                      ),
                      privacyLink: (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={getDisclosureUrl(DisclosureLink.PrivacyPolicy)}
                          onClick={() => track('Privacy Policy Viewed')}
                        >
                          {t(($) => $.common.privacyPolicy)}
                        </a>
                      ),
                    }}
                  />
                )}
              </p>
              {shouldShowSkipOtpLink() && (
                <p>
                  <br />
                  <Trans
                    i18nKey={($) => $.pageOtpRequest.noPhoneApplyHere}
                    components={{
                      applyLink: <SkipOtpLinkButton type="button" onClick={() => validateFormBeforeContinuing(setTouched, validateForm, values)} />,
                    }}
                  />
                </p>
              )}
            </Form>
          )}
        </Formik>
      </FormWrapper>
    </Suspense>
  );
};

export default OTPRequest;
